import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import PhoneInput from './PhoneInput';
import {
  getLabelStyle as fsLabel,
  getInputStyle as fsInput,
  getTextareaStyle as fsTextarea,
  getFieldWrapStyle as fsFieldWrap,
  getRequiredMarkStyle as fsRequired,
  getSubmitStyle,
  getSubmitWrapStyle,
  CheckboxOption,
} from '../pages/LandingPageBuilder/components/formStyles';

const API_BASE = '/api';

interface Props {
  slug: string;            // campaign / tool slug
  blockId: string;
  p: Record<string, any>;  // block props (server re-reads its own copy on submit)
  baseStyle: React.CSSProperties;
  // Which public host serves this block — campaign pages (/campaign/:slug,
  // default) or landing-page tools (/p/:slug). Selects the API namespace.
  apiKind?: 'campaigns' | 'tools';
}

interface RegConfig {
  institution: { name: string; currentYear: string; tierLabels: { level?: string; group?: string }; structureMode: string };
  units: { _id: string; tier: 'LEVEL' | 'GROUP'; name: string; parentUnitId: string | null; order: number }[];
  payment: { enabled: boolean; amount: number };
}

/**
 * Public student-registration form (בלוק רישום תלמיד): fetches the
 * institution's live class list, collects student + parent details per the
 * block's field toggles, and submits a PENDING registration. When the block
 * charges a registration fee the success screen links to /pay/:token.
 */
const StudentRegistrationBlock: React.FC<Props> = ({ slug, blockId, p, baseStyle, apiKind = 'campaigns' }) => {
  const [config, setConfig] = useState<RegConfig | null>(null);
  const [configErr, setConfigErr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState<{ message: string; payment?: { url: string; amount: number } } | null>(null);

  const [student, setStudent] = useState({ firstName: '', lastName: '', idNumber: '', birthDate: '', gender: '', medicalNotes: '', emergencyContact: '' });
  const [parent1, setParent1] = useState({ name: '', phone: '', email: '', idNumber: '', relation: 'father' });
  const [parent2, setParent2] = useState({ name: '', phone: '', email: '', idNumber: '', relation: 'mother' });
  const [showParent2, setShowParent2] = useState(false);
  const [address, setAddress] = useState('');
  const [levelId, setLevelId] = useState('');
  const [unitId, setUnitId] = useState('');
  const [custom, setCustom] = useState<Record<string, any>>({});
  const [honeypot, setHoneypot] = useState('');

  useEffect(() => {
    axios.get(`${API_BASE}/public/${apiKind}/${slug}/registration-config`, { params: { blockId } })
      .then(r => setConfig(r.data))
      .catch(e => setConfigErr(e?.response?.data?.error || 'הטופס אינו זמין כרגע'));
  }, [slug, blockId, apiKind]);

  const fields = p.fields || {};
  const classMode = p.classMode || 'unit';
  const groupLabel = config?.institution?.tierLabels?.group || 'כיתה';
  const levelLabel = config?.institution?.tierLabels?.level || 'שכבה';

  const levels = useMemo(() => (config?.units || []).filter(u => u.tier === 'LEVEL'), [config]);
  const groups = useMemo(() => (config?.units || []).filter(u => u.tier === 'GROUP'), [config]);
  // Two-step picking (שכבה ואז כיתה) whenever the institution really has both
  // tiers; level-only institutions and 'level' mode keep a single select.
  const twoStep = classMode === 'unit' && levels.length > 0 && groups.some(g => !!g.parentUnitId);
  // Classes shown for the chosen level; orphan groups (no parent) stay visible
  // under every level so they're never unreachable.
  const groupsForLevel = useMemo(
    () => groups.filter(g => !g.parentUnitId || g.parentUnitId === levelId),
    [groups, levelId],
  );

  // class options per mode — GROUPs labeled "<level> <group>"; institutions
  // with no groups (level-only) fall back to their LEVELs.
  const unitOptions = useMemo(() => {
    const units = config?.units || [];
    const levelName = (id: string | null) => levels.find(l => l._id === id)?.name || '';
    if (classMode === 'level') return levels.map(l => ({ id: l._id, label: l.name }));
    const list = groups.length ? groups : levels;
    return list.map(u => ({
      id: u._id,
      label: u.tier === 'GROUP' && levelName(u.parentUnitId) ? `${levelName(u.parentUnitId)} ${u.name}` : u.name,
    }));
  }, [config, classMode, levels, groups]);

  const anyField = (field: any) => {
    const key = field.id;
    const val = custom[key];
    const set = (v: any) => setCustom(prev => ({ ...prev, [key]: v }));
    if (field.type === 'textarea') return <textarea rows={3} placeholder={field.placeholder || ''} value={val ?? ''} onChange={e => set(e.target.value)} style={fsTextarea(p, field)} />;
    if (field.type === 'select') return (
      <select value={val ?? ''} onChange={e => set(e.target.value)} style={fsInput(p, field)}>
        <option value="">{field.placeholder || 'בחר...'}</option>
        {(field.options || []).map((o: string, i: number) => <option key={i} value={o}>{o}</option>)}
      </select>
    );
    if (field.type === 'checkbox') return (
      <CheckboxOption checked={!!val} onToggle={() => set(!val)} label={field.placeholder || field.label}
        variant={field.checkboxVariant || p.checkboxVariant || 'square'} color={field.checkboxColor || p.checkboxColor || '#2563eb'}
        size={field.checkboxSize || p.checkboxSize || 18} textColor={p.checkboxLabelColor || '#374151'} textSize={field.fontSize || p.inputFontSize || 14} />
    );
    if (field.type === 'radio') return (
      <div style={{ display: 'flex', flexDirection: (field.optionsLayout || 'column') === 'row' ? 'row' : 'column', flexWrap: 'wrap', gap: 6 }}>
        {(field.options || []).map((o: string, i: number) => (
          <CheckboxOption key={i} isRadio checked={val === o} onToggle={() => set(o)} label={o}
            variant={field.checkboxVariant || p.checkboxVariant || 'square'} color={field.checkboxColor || p.checkboxColor || '#2563eb'}
            size={field.checkboxSize || p.checkboxSize || 18} textColor={p.checkboxLabelColor || '#374151'} textSize={field.fontSize || p.inputFontSize || 14} />
        ))}
      </div>
    );
    if (field.type === 'checkbox-group') {
      const arr: string[] = Array.isArray(val) ? val : [];
      return (
        <div style={{ display: 'flex', flexDirection: (field.optionsLayout || 'column') === 'row' ? 'row' : 'column', flexWrap: 'wrap', gap: 6 }}>
          {(field.options || []).map((o: string, i: number) => {
            const on = arr.includes(o);
            return <CheckboxOption key={i} checked={on} onToggle={() => set(on ? arr.filter(x => x !== o) : [...arr, o])} label={o}
              variant={field.checkboxVariant || p.checkboxVariant || 'square'} color={field.checkboxColor || p.checkboxColor || '#2563eb'}
              size={field.checkboxSize || p.checkboxSize || 18} textColor={p.checkboxLabelColor || '#374151'} textSize={field.fontSize || p.inputFontSize || 14} />;
          })}
        </div>
      );
    }
    if (field.type === 'tel' || field.type === 'phone') return <PhoneInput value={val ?? ''} onChange={set} style={fsInput(p, field)} placeholder={field.placeholder || ''} dir="rtl" required={!!field.required} />;
    if (field.type === 'idNumber') return <input type="text" inputMode="numeric" dir="ltr" maxLength={9} placeholder={field.placeholder || 'תעודת זהות (9 ספרות)'} value={val ?? ''} onChange={e => set(e.target.value.replace(/\D/g, '').slice(0, 9))} style={fsInput(p, field)} />;
    const inputType = field.type === 'email' ? 'email' : field.type === 'number' ? 'number' : 'text';
    return <input type={inputType} placeholder={field.placeholder || ''} value={val ?? ''} onChange={e => set(e.target.value)} style={fsInput(p, field)} />;
  };

  const idInput = (value: string, onChange: (v: string) => void, placeholder: string, field: any = {}) => {
    const invalid = !!value && !/^\d{9}$/.test(value);
    return <input type="text" inputMode="numeric" dir="ltr" maxLength={9} placeholder={placeholder} value={value}
      onChange={e => onChange(e.target.value.replace(/\D/g, '').slice(0, 9))}
      style={{ ...fsInput(p, field), ...(invalid ? { borderColor: '#dc2626' } : {}) }} />;
  };

  const label = (text: string, required?: boolean) => (
    <label style={fsLabel(p, {} as any)}>{text}{required && <span style={fsRequired(p)}>*</span>}</label>
  );

  const submit = async () => {
    setError('');
    if (!student.firstName.trim()) { setError('נא למלא את שם התלמיד/ה'); return; }
    if (!parent1.name.trim() || !parent1.phone.trim()) { setError('נא למלא שם וטלפון של הורה'); return; }
    if (student.idNumber && !/^\d{9}$/.test(student.idNumber)) { setError('תעודת הזהות של התלמיד/ה אינה תקינה'); return; }
    if (twoStep) {
      if (!levelId) { setError(`נא לבחור ${levelLabel}`); return; }
      if (groupsForLevel.length > 0 && !unitId) { setError(`נא לבחור ${groupLabel}`); return; }
    } else {
      if (classMode === 'unit' && unitOptions.length > 0 && !unitId) { setError(`נא לבחור ${groupLabel}`); return; }
      if (classMode === 'level' && unitOptions.length > 0 && !unitId) { setError(`נא לבחור ${levelLabel}`); return; }
    }
    setSubmitting(true);
    try {
      const parents = [parent1, ...(showParent2 && parent2.name.trim() ? [parent2] : [])];
      const { data } = await axios.post(`${API_BASE}/public/${apiKind}/${slug}/register-student`, {
        blockId,
        student: { ...student, birthDate: student.birthDate || undefined },
        parents, address,
        // two-step with a level that has no classes yet → register to the
        // level itself; the office assigns the class on approval.
        requestedUnitId: unitId || (twoStep ? levelId : undefined) || undefined,
        customFields: custom,
        website: honeypot,
      });
      setDone({ message: p.successMessage || data?.message || 'ההרשמה התקבלה!', payment: data?.payment });
    } catch (e: any) {
      setError(e?.response?.data?.error || 'שגיאה בשליחת ההרשמה — נסו שוב');
    } finally {
      setSubmitting(false);
    }
  };

  const card: React.CSSProperties = {
    ...baseStyle,
    backgroundColor: baseStyle.backgroundColor || '#fff',
    boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    overflowY: 'auto',
  };

  if (done) {
    return (
      <div style={{ ...card, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, textAlign: 'center', padding: 24 }} dir="rtl">
        <div style={{ fontSize: 44 }}>🎉</div>
        <div style={{ fontSize: 18, fontWeight: 800 }}>{done.message}</div>
        {done.payment?.url && (
          <>
            <div style={{ fontSize: 14, color: '#6b7280' }}>להשלמת ההרשמה יש לשלם דמי רישום בסך ₪{Math.round(done.payment.amount)}</div>
            <a href={done.payment.url} style={{ ...getSubmitStyle(p), textDecoration: 'none', display: 'inline-block' }}>
              לתשלום דמי רישום — ₪{Math.round(done.payment.amount)}
            </a>
          </>
        )}
      </div>
    );
  }

  if (configErr) {
    return <div style={{ ...card, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 14 }} dir="rtl">{configErr}</div>;
  }

  const gap = p.fieldGap ?? 12;
  return (
    <div style={card} dir="rtl">
      <form style={{ display: 'flex', flexDirection: 'column', gap }} onSubmit={e => { e.preventDefault(); submit(); }}>
        <div style={{ fontSize: 20, fontWeight: 800 }}>
          {p.title || 'רישום תלמיד/ה'}
          {config?.institution?.name ? <span style={{ fontWeight: 500, color: '#6b7280', fontSize: 14 }}> · {config.institution.name}{config.institution.currentYear ? ` · ${config.institution.currentYear}` : ''}</span> : null}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap }}>
          <div>{label('שם פרטי', true)}<input type="text" value={student.firstName} onChange={e => setStudent(s => ({ ...s, firstName: e.target.value }))} style={fsInput(p, {} as any)} /></div>
          <div>{label('שם משפחה', true)}<input type="text" value={student.lastName} onChange={e => setStudent(s => ({ ...s, lastName: e.target.value }))} style={fsInput(p, {} as any)} /></div>
        </div>

        {fields.idNumber && <div>{label('תעודת זהות תלמיד/ה')}{idInput(student.idNumber, v => setStudent(s => ({ ...s, idNumber: v })), 'תעודת זהות (9 ספרות)')}</div>}
        {fields.birthDate && <div>{label('תאריך לידה')}<input type="date" dir="ltr" value={student.birthDate} onChange={e => setStudent(s => ({ ...s, birthDate: e.target.value }))} style={fsInput(p, {} as any)} /></div>}
        {fields.gender && (
          <div>{label('מגדר')}
            <select value={student.gender} onChange={e => setStudent(s => ({ ...s, gender: e.target.value }))} style={fsInput(p, {} as any)}>
              <option value="">בחר...</option><option value="בן">בן</option><option value="בת">בת</option>
            </select>
          </div>
        )}

        {twoStep ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap }}>
            <div>{label(levelLabel, true)}
              <select value={levelId} onChange={e => { setLevelId(e.target.value); setUnitId(''); }} style={fsInput(p, {} as any)}>
                <option value="">בחר...</option>
                {levels.map(l => <option key={l._id} value={l._id}>{l.name}</option>)}
              </select>
            </div>
            <div>{label(groupLabel, !!levelId && groupsForLevel.length > 0)}
              <select value={unitId} onChange={e => setUnitId(e.target.value)} disabled={!levelId || groupsForLevel.length === 0} style={{ ...fsInput(p, {} as any), opacity: !levelId || groupsForLevel.length === 0 ? 0.6 : 1 }}>
                <option value="">
                  {!levelId ? `בחרו ${levelLabel} קודם` : groupsForLevel.length === 0 ? `ההרשמה ל${levelLabel}` : 'בחר...'}
                </option>
                {levelId && groupsForLevel.map(g => <option key={g._id} value={g._id}>{g.name}</option>)}
              </select>
            </div>
          </div>
        ) : (classMode === 'unit' || classMode === 'level') && unitOptions.length > 0 && (
          <div>{label(classMode === 'level' ? levelLabel : groupLabel, true)}
            <select value={unitId} onChange={e => setUnitId(e.target.value)} style={fsInput(p, {} as any)}>
              <option value="">בחר...</option>
              {unitOptions.map(u => <option key={u.id} value={u.id}>{u.label}</option>)}
            </select>
          </div>
        )}

        <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: 8, fontWeight: 700, fontSize: 14 }}>פרטי הורה</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap }}>
          <div>{label('שם ההורה', true)}<input type="text" value={parent1.name} onChange={e => setParent1(x => ({ ...x, name: e.target.value }))} style={fsInput(p, {} as any)} /></div>
          <div>{label('טלפון', true)}<PhoneInput value={parent1.phone} onChange={v => setParent1(x => ({ ...x, phone: v }))} style={fsInput(p, {} as any)} dir="rtl" required /></div>
        </div>
        <div>{label('אימייל')}<input type="email" dir="ltr" value={parent1.email} onChange={e => setParent1(x => ({ ...x, email: e.target.value }))} style={fsInput(p, {} as any)} /></div>
        {fields.parentIdNumber && <div>{label('ת"ז הורה')}{idInput(parent1.idNumber, v => setParent1(x => ({ ...x, idNumber: v })), 'תעודת זהות (9 ספרות)')}</div>}

        {fields.parent2 && !showParent2 && (
          <button type="button" onClick={() => setShowParent2(true)} style={{ background: 'none', border: 'none', color: '#2563eb', fontSize: 13, cursor: 'pointer', textAlign: 'right', padding: 0 }}>
            + הוספת הורה שני
          </button>
        )}
        {fields.parent2 && showParent2 && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap }}>
              <div>{label('שם הורה שני')}<input type="text" value={parent2.name} onChange={e => setParent2(x => ({ ...x, name: e.target.value }))} style={fsInput(p, {} as any)} /></div>
              <div>{label('טלפון')}<PhoneInput value={parent2.phone} onChange={v => setParent2(x => ({ ...x, phone: v }))} style={fsInput(p, {} as any)} dir="rtl" /></div>
            </div>
            <div>{label('אימייל')}<input type="email" dir="ltr" value={parent2.email} onChange={e => setParent2(x => ({ ...x, email: e.target.value }))} style={fsInput(p, {} as any)} /></div>
          </>
        )}

        {fields.address && <div>{label('כתובת')}<input type="text" value={address} onChange={e => setAddress(e.target.value)} style={fsInput(p, {} as any)} /></div>}
        {fields.medical && <div>{label('מידע רפואי (אלרגיות, רגישויות...)')}<textarea rows={2} value={student.medicalNotes} onChange={e => setStudent(s => ({ ...s, medicalNotes: e.target.value }))} style={fsTextarea(p, {} as any)} /></div>}
        {fields.emergencyContact && <div>{label('איש קשר לחירום (שם + טלפון)')}<input type="text" value={student.emergencyContact} onChange={e => setStudent(s => ({ ...s, emergencyContact: e.target.value }))} style={fsInput(p, {} as any)} /></div>}

        {(p.extraFields || []).map((field: any) => (
          <div key={field.id} style={fsFieldWrap(field)}>
            {!field.hideLabel && field.type !== 'checkbox' && (
              <label style={fsLabel(p, field)}>{field.label}{field.required && <span style={fsRequired(p)}>*</span>}</label>
            )}
            {anyField(field)}
          </div>
        ))}

        {/* honeypot — invisible to humans, bots fill it */}
        <input type="text" value={honeypot} onChange={e => setHoneypot(e.target.value)} autoComplete="off" tabIndex={-1}
          style={{ position: 'absolute', left: -9999, width: 1, height: 1, opacity: 0 }} aria-hidden="true" name="website" />

        {config?.payment?.enabled && (
          <div style={{ fontSize: 13, color: '#6b7280', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: 8 }}>
            💳 בסיום ההרשמה יופיע קישור לתשלום דמי רישום בסך ₪{Math.round(config.payment.amount)}
          </div>
        )}
        {error && <div style={{ color: '#dc2626', fontSize: 13, fontWeight: 600 }}>{error}</div>}

        <div style={getSubmitWrapStyle(p)}>
          <button type="submit" disabled={submitting} style={{ ...getSubmitStyle(p), opacity: submitting ? 0.6 : 1 }}>
            {submitting ? 'שולח...' : (p.submitText || 'שליחת הרשמה')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default StudentRegistrationBlock;
